import type { Point } from '../../../types';
import { DrawShape } from './DrawShape';
import type { DrawShapeStyle, Size } from './types';

export class DrawTriangle extends DrawShape {
  readonly type = 'triangle' as const;

  constructor(
    id: string,
    position: Point,
    size: Size,
    style: DrawShapeStyle
  ) {
    super(id, position, size, style);
  }

  private getVertices(): Point[] {
    const { x, y } = this.position;
    const { width, height } = this.size;

    return [
      { x: x + width / 2, y }, // top center
      { x: x + width, y: y + height }, // bottom right
      { x, y: y + height }, // bottom left
    ];
  }

  private distanceToLineSegment(p: Point, v1: Point, v2: Point): number {
    const dx = v2.x - v1.x;
    const dy = v2.y - v1.y;
    const lengthSquared = dx * dx + dy * dy;

    if (lengthSquared === 0) {
      // v1 and v2 are the same point
      const dpx = p.x - v1.x;
      const dpy = p.y - v1.y;
      return Math.sqrt(dpx * dpx + dpy * dpy);
    }

    // Calculate projection of point onto line segment
    const t = Math.max(0, Math.min(1, ((p.x - v1.x) * dx + (p.y - v1.y) * dy) / lengthSquared));
    const projX = v1.x + t * dx;
    const projY = v1.y + t * dy;

    const dpx = p.x - projX;
    const dpy = p.y - projY;
    return Math.sqrt(dpx * dpx + dpy * dpy);
  }

  containsPoint(p: Point): boolean {
    const vertices = this.getVertices();
    const threshold = 10;

    // Check if point is near any edge
    for (let i = 0; i < vertices.length; i++) {
      const v1 = vertices[i]!;
      const v2 = vertices[(i + 1) % vertices.length]!;
      const distance = this.distanceToLineSegment(p, v1, v2);

      if (distance <= threshold) {
        return true;
      }
    }

    return false;
  }

  render(ctx: CanvasRenderingContext2D): void {
    const vertices = this.getVertices();
    const { fill, stroke, strokeWidth, opacity } = this.style;

    ctx.save();
    ctx.globalAlpha = opacity;

    ctx.beginPath();
    ctx.moveTo(vertices[0]!.x, vertices[0]!.y);
    for (let i = 1; i < vertices.length; i++) {
      ctx.lineTo(vertices[i]!.x, vertices[i]!.y);
    }
    ctx.closePath();

    // Fill
    if (fill && fill !== 'transparent') {
      ctx.fillStyle = fill;
      ctx.fill();
    }

    // Stroke
    if (stroke && strokeWidth > 0) {
      ctx.strokeStyle = stroke;
      ctx.lineWidth = strokeWidth;
      ctx.stroke();
    }

    ctx.restore();
  }

  static fromJSON(data: { id: string; position: Point; size: Size; style: DrawShapeStyle }): DrawTriangle {
    return new DrawTriangle(data.id, data.position, data.size, data.style);
  }
}
